import { Link, useNavigate } from "react-router-dom";
import apiAgent from "../../api/apiAgent";
import { tMsg } from "../ToastNotifications";
import { useEffect, useState } from "react";
import _ from "lodash";
import { IListItemProps } from "../interfaces";
import { ImageWithLoading } from "../ImageWithLoading";
import { useSetChangesMade } from "../Publishing/utils";
import { DeletePopup } from "../ConfirmationPopup";
import { useFetchThumbFromMediaItem } from "../../utils";

export const ListItem = ({
  item,
  modelName,
  returnModel,
  returnId,
  refreshList,
  isCard,
  linkParams,
}: IListItemProps): JSX.Element => {
  const navigate = useNavigate();
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
  const fetchThumb = useFetchThumbFromMediaItem();

  useEffect(() => {
    const newThumbSrc = item.thumbnail || item.file || undefined;
    // If thumb is not in local item, check child items.
    if (!newThumbSrc) fetchThumb(modelName, item.id, setImageSrc);
    setImageSrc(newThumbSrc);
  }, [item]);

  const itemName = item.name || item.title || item.cardTitle;

  const userRole = modelName === "users" ? item.user_role : undefined;

  const handleDeletePressed = async () => {
    try {
      if (!item.id) throw "No ID specified.";
      // const request =
      //   modelName === "users"
      //     ? apiAgent.Users.delete(modelName, { username: item.name })
      //     : apiAgent.DataType.delete(modelName, {
      //         targetIdx: _.toNumber(item.id),
      //       });
      const request = apiAgent.DataType.delete({
        model_name: modelName,
        target_ids: [_.toNumber(item.id)],
      });
      const response = await request;
      if (response.status === 200) {
        tMsg.success(`${modelName} deleted.`);
        if (refreshList) refreshList();
      }
    } catch (err) {
      // TODO: add any error in response to notifications here
      tMsg.error(`Error deleting ${modelName}`);
      console.log(err);
    }
  };

  const addParams =
    returnModel && returnId
      ? `?from=${returnModel}&id=${returnId}&${linkParams}`
      : `?${linkParams}`;

  const handleEditPressed = () =>
    navigate(`/m/${modelName}/edit/${item.id}${addParams}`);
  const handleMainNavigate = () =>
    navigate(`/m/${modelName}/show/${item.id}${addParams}`);

  const displayProps = {
    itemName,
    imageSrc,
    userRole,
    handleMainNavigate,
    handleEditPressed,
    handleDeletePressed: () => setShowDeletePopup(true),
  };

  return (
    <>
      {isCard ? (
        <CardListItem {...displayProps} />
      ) : (
        <TextListItem {...displayProps} />
      )}
      <DeletePopup
        confirmAction={() => {
          handleDeletePressed();
          setShowDeletePopup(false);
        }}
        setShowModal={setShowDeletePopup}
        showModal={showDeletePopup}
      />
    </>
  );
};

interface IItemDisplayProps {
  imageSrc: string | undefined;
  itemName: string | undefined;
  userRole: string | undefined;
  handleMainNavigate: () => void;
  handleEditPressed: () => void;
  handleDeletePressed: () => void;
}

const CardListItem = ({
  imageSrc,
  itemName,
  userRole,
  handleMainNavigate,
  handleEditPressed,
  handleDeletePressed,
}: IItemDisplayProps) => (
  <div className="transition w-48 h-64 group relative">
    <div
      onClick={() => handleMainNavigate()}
      className="flex flex-col w-full h-full cursor-pointer"
    >
      <div className="h-3/4 bg-black w-full grid pointer-events-none">
        <img
          src={imageSrc || "/assets/default-image.jpg"}
          className="w-full h-full object-cover"
        />
      </div>
      {userRole ? (
        <div className="flex flex-col bg-grayBg w-full h-1/4 text-white text-sm font-medium">
          <div className="grid place-content-center">{itemName}</div>
          <div className="grid place-content-center">{userRole}</div>
        </div>
      ) : (
        <div className="bg-grayBg w-full h-1/4 grid place-content-center text-white text-sm font-medium">
          {itemName}
        </div>
      )}
      <div className="transition absolute top-0 left-0 w-full h-full bg-black/10 opacity-0 group-hover:opacity-100 pointer-events-none" />
    </div>
    <div className="transition absolute top-0 right-0 flex opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto">
      <div
        className="text-black bg-white hover:bg-[#EEEEEE] p-3 cursor-pointer"
        onClick={() => handleEditPressed()}
      >
        <i className="icon-edit" />
      </div>
      <div
        className="text-warningRed bg-white hover:bg-[#EEEEEE] p-3 cursor-pointer"
        onClick={() => handleDeletePressed()}
      >
        <i className="icon-delete" />
      </div>
    </div>
  </div>
);

const TextListItem = ({
  imageSrc,
  itemName,
  handleMainNavigate,
  handleEditPressed,
  handleDeletePressed,
}: IItemDisplayProps) => (
  <div className="transition w-full h-12 text-left">
    <div className="flex w-full cursor-pointer">
      {imageSrc && (
        <ImageWithLoading
          src={imageSrc}
          className="max-w-[3.5rem] h-12 object-fit"
        />
      )}
      <div
        className="flex-grow bg-grayBg text-white px-4 grid content-center relative group"
        onClick={() => handleMainNavigate()}
      >
        {itemName}
        <div className="transition absolute top-0 left-0 w-full h-full bg-black/10 opacity-0 group-hover:opacity-100 pointer-events-none" />
      </div>
      <div
        className="text-black bg-grayBg hover:bg-[#EEEEEE] p-3"
        onClick={() => handleEditPressed()}
      >
        <i className="icon-edit" />
      </div>
      <div
        className="text-warningRed bg-grayBg hover:bg-[#EEEEEE] p-3"
        onClick={() => handleDeletePressed()}
      >
        <i className="icon-delete" />
      </div>
    </div>
  </div>
);
